import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import dragula from "dragula";
import "dragula/dist/dragula.css";
import { all_routes } from "../../router/all_routes";
// Botão "Ligar" da discadora (04/11/2026): só aparece na conta com chave e para quem tem atendente ligado.
import BotaoLigarDiscadora from "../../application/discadora/BotaoLigar";

interface KanbanProps {
  stages: any[];
  leads: any[];
  onMoveLead: (fone: string, novaEtapa: string, sourceEtapa: string) => void;
  onOpenCard: (lead: any) => void;
  onOpenChat: (phone: string, name: string) => void;
}

const KanbanDisplayV2: React.FC<KanbanProps> = ({ stages, leads, onMoveLead, onOpenCard, onOpenChat }) => {
  const isDark = useSelector((state: any) => state.themeSetting.dataTheme) === "dark";
  const containersRef = useRef<HTMLDivElement[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Ref estável para evitar recriar o dragula quando o callback muda
  const onMoveLeadRef = useRef(onMoveLead);
  onMoveLeadRef.current = onMoveLead;

  const leadsByStage = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    stages.forEach(s => groups[s.nome_funil] = []);
    leads.forEach(l => {
        if (groups[l.Tags]) groups[l.Tags].push(l);
    });
    return groups;
  }, [leads, stages]);

  useEffect(() => {
    const containers = containersRef.current.filter(Boolean);
    if (containers.length === 0) return;
    const drake = dragula(containers);

    // Auto-scroll horizontal durante o drag
    let autoScrollRAF: number | null = null;
    const EDGE_ZONE = 80; // px da borda que ativa o scroll
    const SCROLL_SPEED = 70; // px por frame

    const handleMouseMove = (e: MouseEvent) => {
      const wrapper = scrollRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const x = e.clientX;

      if (autoScrollRAF) cancelAnimationFrame(autoScrollRAF);

      const scrollStep = () => {
        if (!scrollRef.current) return;
        if (x < rect.left + EDGE_ZONE) {
          scrollRef.current.scrollLeft -= SCROLL_SPEED;
          autoScrollRAF = requestAnimationFrame(scrollStep);
        } else if (x > rect.right - EDGE_ZONE) {
          scrollRef.current.scrollLeft += SCROLL_SPEED;
          autoScrollRAF = requestAnimationFrame(scrollStep);
        }
      };
      scrollStep();
    };

    drake.on("drag", () => {
      document.addEventListener("mousemove", handleMouseMove);
    });

    drake.on("dragend", () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (autoScrollRAF) { cancelAnimationFrame(autoScrollRAF); autoScrollRAF = null; }
    });

    drake.on("drop", (el, target, source) => {
      if (!el || !target || !source) return;
      const leadFone = el.getAttribute("data-fone");
      const newStage = target.getAttribute("data-stage");
      const oldStage = source.getAttribute("data-stage");

      // Cancela o movimento do DOM — React redesenha via estado
      drake.cancel(true);

      if (leadFone && newStage && oldStage && newStage !== oldStage) {
        onMoveLeadRef.current(leadFone, newStage, oldStage);
      }
    });

    return () => {
      drake.destroy();
      document.removeEventListener("mousemove", handleMouseMove);
      if (autoScrollRAF) cancelAnimationFrame(autoScrollRAF);
    };
  }, [stages]); // Só recria o drake quando as stages mudam

  // --- Modern Admin Aesthetic (Pixinvent Style) ---
  
  const wrapperStyle: React.CSSProperties = {
    height: "calc(100vh - 220px)",
    background: "transparent", 
    padding: "0 20px 20px 20px",
    display: "flex",
    gap: "16px",
    overflowX: "auto",
    scrollBehavior: "smooth",
    WebkitOverflowScrolling: "touch" // Melhora o scroll no iOS
  };

  const columnStyle: React.CSSProperties = {
    minWidth: window.innerWidth < 768 ? "calc(100vw - 60px)" : "260px",
    maxWidth: window.innerWidth < 768 ? "calc(100vw - 60px)" : "260px",
    background: isDark ? "#1F2937" : "#f2f4f7",
    borderRadius: "12px",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    maxHeight: "100%",
    border: isDark ? "1px solid #2F343C" : "1px solid #e2e8f0"
  };

  const cardStyle: React.CSSProperties = {
    background: isDark ? "#0D0D0D" : "#ffffff",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "8px",
    border: isDark ? "1px solid #2F343C" : "1px solid #e2e8f0",
    cursor: "grab",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.2)" : "0 2px 4px rgba(0,0,0,0.02)",
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  };

  return (
    <div className="kanban-wrapper" style={wrapperStyle} ref={scrollRef}>
      {stages.map((stage, idx) => (
        <div key={stage.id} className="kanban-column" style={columnStyle}>
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <div className="d-flex align-items-center gap-1">
              <h6 style={{
                  margin: 0,
                  fontWeight: 700,
                  color: isDark ? "#D8DFEE" : "#475569",
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em"
              }}>
                {stage.nome_funil}
              </h6>
              {stage.atendimento_humano === "Sim" && (
                <i className="ti ti-user-check" style={{ color: "#2e7d32", fontSize: "14px" }} title="Atendimento Humano" />
              )}
              {!!stage.closer && stage.atendimento_humano !== "Sim" && (
                <i className="ti ti-robot" style={{ color: "#e65100", fontSize: "14px" }} title="Closer (Bot)" />
              )}
            </div>
            <span style={{
                background: isDark ? "#374151" : "#e2e8f0",
                color: isDark ? "#D8DFEE" : "#475569",
                borderRadius: "6px",
                fontSize: "10px",
                padding: "1px 6px",
                fontWeight: 700
            }}>
              {leadsByStage[stage.nome_funil]?.length || 0}
            </span>
          </div>
          
          <div
            className="kanban-drag-area"
            ref={el => { if (el) containersRef.current[idx] = el; }}
            data-stage={stage.nome_funil}
            style={{ flex: 1, overflowY: "auto", paddingRight: "4px" }}
          >
            {leadsByStage[stage.nome_funil]?.map(lead => {
              const isHuman = stage.atendimento_humano === "Sim";
              const isCloser = !!stage.closer && !isHuman;

              return (
                <div
                  key={lead.key}
                  className="kanban-card-modern"
                  data-fone={lead.FullPhone || lead.Phone}
                  style={cardStyle}
                  onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = isDark ? "0 10px 15px -3px rgba(0, 0, 0, 0.4)" : "0 10px 15px -3px rgba(0, 0, 0, 0.1)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = isDark ? "0 2px 4px rgba(0,0,0,0.2)" : "0 2px 4px rgba(0,0,0,0.02)";
                      e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center gap-1" style={{ overflow: "hidden", flex: 1 }}>
                          {isHuman && (
                            <i className="ti ti-user-check" style={{ color: "#2e7d32", fontSize: "13px", flexShrink: 0 }} title="Atendimento Humano" />
                          )}
                          {isCloser && (
                            <i className="ti ti-robot" style={{ color: "#e65100", fontSize: "13px", flexShrink: 0 }} title="Closer (Bot)" />
                          )}
                          {(() => {
                              const rawPhone = lead.Phone || "";
                              const phoneState = rawPhone.startsWith("55") ? rawPhone : "55" + rawPhone;
                              return (
                                <Link
                                    to={all_routes.chat}
                                    state={{ phone: phoneState }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    title="Abrir conversa no chat"
                                    style={{
                                        display: "block",
                                        fontWeight: 600,
                                        color: isDark ? "#D8DFEE" : "#1e293b",
                                        fontSize: "13px",
                                        lineHeight: "1.2",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                >
                                    {lead.LeadName}
                                </Link>
                              );
                          })()}
                      </div>
                      {/* MiniChat desabilitado temporariamente
                      <div
                          style={{ color: "#22c55e", cursor: "pointer", fontSize: "16px", flexShrink: 0 }}
                          onClick={() => onOpenChat(lead.Phone, lead.LeadName)}
                      >
                          <i className="ti ti-brand-whatsapp" />
                      </div>
                      */}
                  </div>

                  <div style={{ color: isDark ? "#9CA3AF" : "#64748b", fontSize: "11px", fontWeight: 500 }}>
                      {lead.Phone}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-1 pt-1" style={{ borderTop: isDark ? "1px solid #2F343C" : "1px solid #f8fafc" }}>
                      <div style={{ fontSize: "10px", color: isDark ? "#6B7280" : "#94a3b8", display: "flex", alignItems: "center", gap: "2px" }}>
                          <i className="ti ti-calendar-event" />
                          {(() => {
                              if (!lead.Data) return "—";
                              const d = new Date(lead.Data);
                              d.setDate(d.getDate() + 1);
                              return d.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit' });
                          })()}
                      </div>
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                      <BotaoLigarDiscadora fone={lead.FullPhone || lead.Phone} idTime={lead.IdTime} />
                      <button
                          onClick={() => onOpenCard(lead)}
                          style={{
                              background: "transparent",
                              border: "none",
                              color: "#007aff",
                              fontSize: "10px",
                              fontWeight: 700,
                              padding: "0",
                              cursor: "pointer",
                              textTransform: "uppercase"
                          }}
                      >
                          Ver Card
                      </button>
                      </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <style>{`
        /* Scroll das Colunas (Vertical) */
        .kanban-drag-area::-webkit-scrollbar { width: 6px; }
        .kanban-drag-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        [data-theme="dark"] .kanban-drag-area::-webkit-scrollbar-thumb { background: #374151; }

        /* Scroll do Kanban (Horizontal) */
        .kanban-wrapper::-webkit-scrollbar { height: 18px; }
        .kanban-wrapper::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.08);
          border-radius: 20px;
          margin: 0 20px;
        }
        [data-theme="dark"] .kanban-wrapper::-webkit-scrollbar-track { background: rgba(255,255,255,0.06); }
        .kanban-wrapper::-webkit-scrollbar-thumb {
          background: #64748b;
          border-radius: 20px;
          border: 4px solid transparent;
          background-clip: content-box;
        }
        .kanban-wrapper::-webkit-scrollbar-thumb:hover {
          background: #007aff;
          border: 2px solid transparent;
        }
      `}</style>
    </div>
  );
};
export default KanbanDisplayV2;